import React, { useState } from "react";
import { Rocket, Building2, TrendingUp, Compass } from "lucide-react";
import DiscoverLayout from "../../components/discover/DiscoverLayout";
import CategoryCard from "../../components/discover/CategoryCard";

export default function DiscoverPortalPage() {
  const [stats] = useState({
    startups: "100+",
    incubators: "6 Program Hubs",
    vcs: "8 Active Funds",
  });

  const sections = [
    {
      title: "Directory",
      description: "Connect with the primary entities of the Indian tech ecosystem.",
      categories: [
        { title: "Startups", description: "Discover high-growth startups founded and headquartered in India.", icon: Rocket, to: "/discover/startups", stats: stats.startups },
        { title: "Incubators", description: "Explore accelerators, incubators, and university startup programs.", icon: Building2, to: "/discover/incubators", stats: stats.incubators },
        { title: "VC Firms", description: "Venture capital firms and angel networks investing in India.", icon: TrendingUp, to: "/discover/vc-firms", stats: stats.vcs },
      ],
    },
    {
      title: "Insights",
      description: "Analyze market indicators, growth trajectories, and funding trends.",
      categories: [
        { title: "Trending Startups", description: "Breakout companies showcasing high YoY growth and MAUs.", icon: TrendingUp, to: "/discover/trending-startups", stats: "Breakout" },
      ],
    },
    {
      title: "Resources",
      description: "Read, learn, and find active opportunities in scaling teams.",
      categories: [
        { title: "Explore Industries", description: "Browse startups categorized by industry sectors and tech models.", icon: Compass, to: "/discover/industries", stats: "Sectors" },
      ],
    },
  ];

  return (
    <DiscoverLayout
      title="Indian Startup Ecosystem Portal"
      subtitle="Your premium navigation hub to browse startups, accelerators, venture capital firms, funding announcements, and founder playbooks."
    >
      <div className="space-y-12">
        {/* Directory Section */}
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-bold text-gray-950 font-display tracking-tight flex items-center gap-2">
              Directory
            </h2>
            <p className="text-xs text-gray-500 mt-1">{sections[0].description}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {sections[0].categories.map((cat, catIdx) => (
              <CategoryCard
                key={catIdx}
                title={cat.title}
                description={cat.description}
                icon={cat.icon}
                to={cat.to}
                stats={cat.stats}
              />
            ))}
          </div>
        </div>

        {/* Insights, Resources & Statistics Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Insights */}
          <div className="space-y-4 flex flex-col">
            <div>
              <h2 className="text-xl font-bold text-gray-950 font-display tracking-tight flex items-center gap-2">
                Insights
              </h2>
              <p className="text-xs text-gray-500 mt-1">{sections[1].description}</p>
            </div>
            <div className="flex-1 grid grid-cols-1 gap-6">
              {sections[1].categories.map((cat, catIdx) => (
                <CategoryCard
                  key={catIdx}
                  title={cat.title}
                  description={cat.description}
                  icon={cat.icon}
                  to={cat.to}
                  stats={cat.stats}
                />
              ))}
            </div>
          </div>

          {/* Resources */}
          <div className="space-y-4 flex flex-col">
            <div>
              <h2 className="text-xl font-bold text-gray-950 font-display tracking-tight flex items-center gap-2">
                Resources
              </h2>
              <p className="text-xs text-gray-500 mt-1">{sections[2].description}</p>
            </div>
            <div className="flex-1 grid grid-cols-1 gap-6">
              {sections[2].categories.map((cat, catIdx) => (
                <CategoryCard
                  key={catIdx}
                  title={cat.title}
                  description={cat.description}
                  icon={cat.icon}
                  to={cat.to}
                  stats={cat.stats}
                />
              ))}
            </div>
          </div>

          {/* Statistics */}
          <div className="space-y-4 flex flex-col">
            <div>
              <h2 className="text-xl font-bold text-gray-950 font-display tracking-tight flex items-center gap-2">
                Statistics
              </h2>
              <p className="text-xs text-gray-500 mt-1">Key metrics across the ecosystem network.</p>
            </div>
            {/* Ecosystem stats */}
            <div className="flex-1 bg-white border border-gray-150 rounded-[20px] p-6 shadow-sm relative overflow-hidden flex flex-col justify-between">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#7C3AED]/5 to-[#EC4899]/5 rounded-full blur-xl pointer-events-none" />
              <h3 className="text-xs font-bold text-gray-900 uppercase tracking-widest mb-4">
                Ecosystem Numbers
              </h3>
              <div className="space-y-4 flex-1 flex flex-col justify-around">
                <div>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Startups</p>
                  <p className="text-2xl font-extrabold text-[#7C3AED] font-display">100+</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Accelerators & Hubs</p>
                  <p className="text-lg font-bold text-gray-800">6 Major Hubs</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">VC Funds</p>
                  <p className="text-lg font-bold text-gray-800">8 Curated Funds</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DiscoverLayout>
  );
}
